import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatabaseService } from './database.service';
import { Program } from '../models/program';
import { map, Observable, take, tap } from 'rxjs';
import { AppConfigService } from '../app-config.service';

@Injectable({
  providedIn: 'root'
})
export class ProgramService {

  private canadianIndicator: string = "Province";
  private canadianWord: string = "Canadian";

  private decodedURIValues = {
    "%2C":",",
    "%3A":":",
    "%26":"&",
    "%3B":";"
  }

  constructor(
    private http: HttpClient,
    private databaseService: DatabaseService,
    private configService: AppConfigService
    
  ) {
    this.databaseService = databaseService;
    this.http.get(configService.getCsvFolderLocation() + "programs.csv", { responseType: 'text' }).subscribe({
      next: (data: string) => {
        this.parseData(data);
      },
      error: (error: any) => {
      }
    })

  }

  /**
   * Converts the CSV into data that can be inserted to the on-memory database
   * @param toConvert The contents of the CSV
   */
  private parseData(toConvert: string) {
    let me = this;
    toConvert = toConvert.replaceAll("\r", "");

    let rows: Array<Array<string>> = toConvert.split("\n")
      .filter(line => line.length > 0).slice(1)
      .map(function (row) { return row.split(",") });

    let bulkArray:Program[] = [];
    rows.forEach(function (row) {
      try {
        if(!(row.length === 1 && row[0] === "")){
          bulkArray.push({
            study: me.decodeCsvField(row[0]),
            college: me.decodeCsvField(row[1]),
            program: me.decodeCsvField(row[2]),
            costDomestic: Number(me.decodeCsvField(row[3]) ?? 0),
            costOutOfProvince: Number(me.decodeCsvField(row[4]) ?? 0),
            costInternational: Number(me.decodeCsvField(row[5]) ?? 0),
            residency: me.decodeCsvField(row[6]),
            firstYearCost: Number(me.decodeCsvField(row[7]) ?? 0),
            bookSupplyCost: Number(me.decodeCsvField(row[8]) ?? 0),
            modifyCourseLoad: Boolean(me.decodeCsvField(row[9]) === 'Y')
          });
        }
      } catch (error) {
        console.error("Skipping malformed programs.csv row", row, error);
      }
    });

    if(bulkArray.length > 0){
      try {
        me.databaseService.db.program.bulkUpsert(bulkArray as any[]).then(result => {
            result.error.forEach(err => console.error("Skipping malformed programs.csv row", err));
          })
          .catch(error => console.error("Error bulk inserting programs array: ", error));
      } catch (error) {
        console.error("Error bulk inserting programs array: ", error);
      }
    }
  }

  /**
   * Gets a list of studies a user can access based on their studies and residency
   * @param selectedStudies Graduate or undergraduate
   * @param selectedResidency International, in province, or out of province
   * @returns Observable Array<String> a list of studies that a user can access
   */
  public getStudies(selectedStudies?: string, selectedResidency?: string): Observable<string[]> {
    if (selectedResidency?.includes(this.canadianIndicator)) {
      return this.databaseService.db.program.find(
        {
          selector: {
            study: selectedStudies,
            $or: [{
              residency: "All"
            },
            {
              residency: selectedResidency
            },
            {
              residency: this.canadianWord
            }]
          },
          sort:[
            {college: 'asc'}
          ]
        }
      ).$
        .pipe(
          map(values => [... new Set(values.map(value => value.college))])
        );
    } else {
      return this.databaseService.db.program.find(
        {
          selector: {
            study: selectedStudies,
            $or: [{
              residency: "All"
            },
            {
              residency: selectedResidency
            }],
          },      
          sort:[
            {college: 'asc'}
          ]
        }
      ).$
        .pipe(
          map(values => [... new Set(values.map(value => value.college))])
        );
    }

  }

  /**
   * Gets a list of programs
   * @param selectedStudies 
   * @param selectedCollege 
   * @param selectedResidency 
   * @returns Observable Array<String> that shows a list of programs that the user 
   */
  public getPrograms(selectedStudies?: string, selectedCollege?: string, selectedResidency?: string): Observable<string[]> {
    if (selectedResidency?.includes(this.canadianIndicator)) {
      return this.databaseService.db.program.find(
        {
          selector: {
            study: selectedStudies,
            college: selectedCollege,
            $or: [{
              residency: "All"
            }, {
              residency: selectedResidency
            },
            {
              residency: this.canadianWord
            }]
          },
          sort:[
            {program: 'asc'}
          ]
        }
      ).$.pipe(
        map(values => [... new Set(values.map(value => value.program))])
      );
    } else {
      return this.databaseService.db.program.find(
        {
          selector: {
            study: selectedStudies,
            college: selectedCollege,
            $or: [{
              residency: "All"
            }, {
              residency: selectedResidency
            }]
          },
          sort:[
            {program: 'asc'}
          ]
        }
      ).$.pipe(
        map(values => [... new Set(values.map(value => value.program))])
      );
    }
  }

  /**
   * Gets a program and its information based on what a user selected
   * @param selectedStudies 
   * @param selectedCollege 
   * @param selectedResidency 
   * @param selectedProgramName 
   * @returns Observable<Program> which is a selected program that contains information such as cost of program for the first year
   */
  public getSelectedProgram(selectedStudies?: string, selectedCollege?: string, selectedResidency?: string, selectedProgramName?: string) {
    if (selectedResidency?.includes(this.canadianIndicator)) {
      return this.databaseService.db.program.findOne({
        selector: {
          study: selectedStudies,
          college: selectedCollege,
          $or: [{
            residency: "All"
          }, {
            residency: selectedResidency
          },
          {
            residency: this.canadianWord
          }
          ],
          program: selectedProgramName
  
        }
      }).$.pipe(
        take(1),
        map(result => {
          let toReturn: Program = {
            program: result?.program,
            college: result?.college,
            study: result?.study,
            costInternational: result?.costInternational,
            costOutOfProvince: result?.costOutOfProvince,
            costDomestic: result?.costDomestic,
            residency: result?.residency,
            firstYearCost: result?.firstYearCost,
            bookSupplyCost: result?.bookSupplyCost,
            modifyCourseLoad: result?.modifyCourseLoad
          }
          return toReturn;
        })
      );
    } else {
      return this.databaseService.db.program.findOne({
        selector: {
          study: selectedStudies,
          college: selectedCollege,
          $or: [{
            residency: "All"
          }, {
            residency: selectedResidency
          }
          ],
          program: selectedProgramName
  
        }
      }).$.pipe(
        take(1),
        map(result => {
          let toReturn: Program = {
            program: result?.program,
            college: result?.college,
            study: result?.study,
            costInternational: result?.costInternational,
            costOutOfProvince: result?.costOutOfProvince,
            costDomestic: result?.costDomestic,
            residency: result?.residency,
            firstYearCost: result?.firstYearCost,
            bookSupplyCost: result?.bookSupplyCost,
            modifyCourseLoad: result?.modifyCourseLoad
          }
          return toReturn;
        })
      );
    }


  }

  /**
   * Helper function that removes URI encoding in the CSV file. This was done to prevent weird things if user puts commas into information
   * @param raw the raw CSV file
   * @returns decoded string
   */
  private decodeCsvField(raw: string):string {
    return decodeURI(raw ?? "").replace(/%2C|%3A|%26|%3B/g, value => this.decodedURIValues[value as keyof typeof this.decodedURIValues]);
  }
}


